import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client';
import jwt_decode, { JwtPayload } from "jwt-decode";

import { environment } from '../../../../environments';

import { BehaviorSubject, throwError as _throw, Observable, of } from 'rxjs';
import { catchError, delay, map, tap } from 'rxjs/operators';


import { TranslateService } from '@ngx-translate/core';

import { Router } from '@angular/router';
import { UserModel } from '../_models/user.model';
import { Permission } from '../_models/permission.model';
import { permissions } from '../permissions/permissions';
import { PermissionType } from '../enums/permission.enum';
import { MVLocalStorageService } from '../../../services/mv-local-storage.service';
import { PeriodTypes } from '@app/enums/periods.enum';
import { AddUserPeriodicityPreferenceDto } from '@app/models/AddUserPeriodicityPreference.model';
import { ModulesEnum } from '../enums/module.enum';
import { SessionItems } from '@app/enums/constants.enum';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Private
  private apiEndPoint: string;
  private _currentUserSubject: BehaviorSubject<UserModel>;

  private manager = new UserManager(getClientSettings());
  private user: User = null;

  // Public
  public currentUser$: Observable<UserModel>;

  /**
  * Constructor
  * 
  * @param {HttpClient} _http
  * @param {TranslateService} _translate
  */
  constructor(
    private router: Router,
    private _http: HttpClient,
    private _translate: TranslateService,
  ) {
    // Initialize the service
    this._init();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get currentUser(): UserModel {
    return this._currentUserSubject.value;
  }

  get userId(): number {
    return (this.currentUser as any).userId;
  }

  get currentUserName(): string {
    try {
      return this.currentUser.userName;
    } catch {
      return 'N/A';
    }
  }

  get currentUserPublic(): any {
    try {
      return this.currentUser;
    } catch {
      return 'N/A';
    }
  }

  get userCanInitConversation(): boolean {
    const currentUserPermissions = this._currentUserSubject.value.permissions.map(p => p.id);
    return currentUserPermissions.includes(PermissionType.EvaluationInitConversation);
  }

  get isDueDiligenceProvider(): boolean {
    const currentUserPermissions = this._currentUserSubject.value.permissions.map(p => p.id);
    return currentUserPermissions.includes(PermissionType.DueDiligenceProvider);
  }
  get isDueDiligenceClient(): boolean {
    const currentUserPermissions = this._currentUserSubject.value.permissions.map(p => p.id);
    return currentUserPermissions.includes(PermissionType.DueDiligenceClient);
  }

  get isThirdParty(): boolean {
    const thirdpartyPermissions = [
      PermissionType.QuestionnairesRequestedByTenantList,
      PermissionType.QuestionnairesRequestedByTenantReply,
      PermissionType.TasksRequestedByTenantList,
      PermissionType.ReplyGeneralTaskRequested,
      PermissionType.ReplyUserTaskRequested,
    ];

    const currentUserPermissions = this._currentUserSubject.value.permissions.map(p => p.id);
    return thirdpartyPermissions.every(p => currentUserPermissions.includes(p));
  }

  get canHaveConversation(): boolean {
    const permissions = [
      PermissionType.QuestionnairesRequestedByTenantList,
      PermissionType.QuestionnairesRequestedByTenantReply,
      PermissionType.ThirdPartyExploreEvaluation,
      PermissionType.ThirdPartyContinueEvaluation
    ];

    const currentUserPermissions = this._currentUserSubject.value.permissions.map(p => p.id);
    return permissions.some(p => currentUserPermissions.includes(p));
  }

  get currentUserMaxInputLength(): number {
    //#ToDo: Get value from userModel
    return 255;
  }

  get notificationRefreshTime(): number {
    return this._currentUserSubject.getValue().secondsRefreshNotifications;
  }

  get maxUploadFileSize(): number {
    return this._currentUserSubject.getValue().maxSizeFile;
  }

  get allowedFileExtensions(): string[] {
    return this._currentUserSubject.getValue().fileExtensionAvailables;
  }

  get currentUserLanguage(): string {
    return MVLocalStorageService.getItem('language');
  }


  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Initialize
   *
   * @private
   */
  private _init(): void {

    this.manager.getUser().then(user => {
      this.user = user;
    });

    // Set initial value
    this.apiEndPoint = environment.apiUrl;

    this._currentUserSubject = new BehaviorSubject<UserModel>(JSON.parse(MVLocalStorageService.getItem('current_user')));
    this.currentUser$ = this._currentUserSubject.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  ping(ping: number): Observable<any> {
    return this._http
      .get(`${this.apiEndPoint}/authentication/ping/${ping}`);
  }

  getEmailClaim() : string {
		const cloudEmailKey = environment.cloudEmailKey;
		let email: string = this.getClaims()[cloudEmailKey];
		if (!email){
      email = this.getClaims()["email"];
    }
    if (!email && this.user.access_token) {
      var accesToken = this.user.access_token
      const decodedToken = jwt_decode<JwtPayload>(accesToken)
      email = decodedToken[cloudEmailKey];
    }
    return email.toLowerCase();
  }

  login(): Observable<any> {
    let applicationId = environment.applicationId;
    let tenantId = environment.tenantId;
    let headers = new HttpHeaders({
      Authorization: this.getAuthorizationHeaderValue(),
      TenantId: tenantId.toString()
    });
    const email = this.getEmailClaim();

    return this._http
      .post<any>(`${this.apiEndPoint}/authentication/authorize`, { email, applicationId, tenantId }, { headers: headers })
      .pipe(
        map(user => {
          if (user) {
            MVLocalStorageService.setItem('current_user', JSON.stringify(user));
            this._currentUserSubject.next(user);


            if (user.requiresSignDisclaimer) {
              this.router.navigate(['disclaimer'])
            } else if (this.redirectUrl) {
              this.router.navigate([this.redirectUrl]);
            }
            else {
              this.router.navigate(['/app']);
            }
          } else {
            this.router.navigate(['/unauthorized']);
          }
        }),
        catchError(err => {
          if (err.errorcode) {
            return _throw(err.errorcode);
          }
          this.clearData();
          this.router.navigate(['/unauthorized']);
        })
      );
  }

  logout(): void {
    if (this.currentUser) {
      let applicationId = environment.applicationId;
      let tenantId = environment.tenantId;
      let headers = new HttpHeaders({
        Authorization: this.getAuthorizationHeaderValue(),
        TenantId: tenantId.toString()
      })

      this._http.post(`${this.apiEndPoint}/authentication/logout`, { headers: headers })
        .pipe(
          map(res => {
            // remove user from local storage to log user out
            MVLocalStorageService.removeItem('current_user');
            MVLocalStorageService.removeItem('signed_disclaimer');
            this.clearData();
            this._currentUserSubject.next(null);
            this.manager.signoutRedirect();
          }),
          catchError(err => {
            if (err.errorcode) {
              return _throw(err.errorcode);
            }
            this.router.navigate(['/unauthorized']);
          })
        ).subscribe();
    }
    else {
      // remove user from local storage to log user out
      MVLocalStorageService.removeItem('current_user');
      MVLocalStorageService.removeItem('signed_disclaimer');
      this.clearData();
      this._currentUserSubject.next(null);
      this.manager.signoutRedirect();
    }
  }

  clearUser(): void {
    // remove user from local storage to log user out
    MVLocalStorageService.removeItem('current_user');
    MVLocalStorageService.removeItem('signed_disclaimer');
    this._currentUserSubject.next(null);
    this.clearData();
  }

  enforceLogin(pMessage) {
    MVLocalStorageService.removeItem('current_user');
    MVLocalStorageService.removeItem('signed_disclaimer');
    this._currentUserSubject.next(null);
    if (pMessage == 'ErrUserNotFound' || pMessage == 'DoNotHavePermissionsForThisApplication') {
      this.router.navigate(['/unauthorized']);
    }
    else {
      this.router.navigate(['/logout-callback'], { queryParams: { errorMessage: 'La sesión expiró' } });
    }
  }

  isLoggedIn(): Promise<boolean> {
    return this.manager.getUser().then(user => user && !user.expired);
  }

  tryToAuthorizeUser(): void {
    if (!this.currentUser) {
      this.login()
        .subscribe();
    }
  }
  getClaims() {
    return this.user.profile;
  }

  getAuthorizationHeaderValue(): string {
    if (this.user.profile.tokenName == "access_token") {
      return `${this.user.token_type} ${this.user.access_token}`;
    }
    else if (this.user.profile.tokenName == "id_token") {
      return `${this.user.token_type} ${this.user.id_token}`;
    }
    else if (this.user.profile.tokenName == "refresh_token") {
      return `${this.user.token_type} ${this.user.refresh_token}`;
    }
    else if (this.user.profile.tokenName === undefined) {
      if (environment.accessTokenType == "access_token")
        return `${this.user.token_type} ${this.user.access_token}`;
      else
        return `${this.user.token_type} ${this.user.id_token}`;

    }
    return `${this.user.token_type} ${this.user.access_token}`;
  }

  startAuthentication(): Promise<void> {
    return this.manager.signinRedirect();
  }

  completeAuthentication(): Promise<void> {
    return this.manager.signinRedirectCallback().then(user => {
      this.user = user;
    });
  }

  isAuthorized(permissionId: number | number[]): boolean {
    if (!this.currentUser || !this.currentUser.permissions) {
      return false;
    }

    if (Array.isArray(permissionId))
      return this.currentUser.permissions.some(p => permissionId.includes(p.id));
    else
      return this.currentUser.permissions.some(p => p.id === permissionId);
  }

  isAuthorizedWithAllPermissions(permissionId: number | number[]): boolean {
    if (!this.currentUser || !this.currentUser.permissions) {
      return false;
    }

    if (Array.isArray(permissionId)) {
      let hasPermissions = true;
      permissionId.forEach(permId => {
        if (!this.currentUser.permissions.some(p => p.id === permId)) hasPermissions = false;
      });
      return hasPermissions;
    }
    else
      return this.currentUser.permissions.some(p => p.id === permissionId);
  }

  async isSessionValid() {
    if (await this.isLoggedIn()) {
      const email = this.getEmailClaim();
      return this.currentUser && ((this.currentUser.email.toLowerCase()) == email);
    }
    return false;
  }

  needsToSignDisclaimer() {
    return this.currentUser.requiresSignDisclaimer && !MVLocalStorageService.getItem('signed_disclaimer');
  }

  completeDisclaimerSigning() {
    MVLocalStorageService.setItem('signed_disclaimer', true);
    let route = this.redirectUrl || '/app';
    return this.router.navigate([route]);
  }

  getPermissions(): Observable<Permission[]> {
    return this.currentUser$
      .pipe(
        delay(15000),
        // filter((user: UserModel) => user !== null && user.permissions && user.permissions.length > 0),
        map((user: UserModel) => {
          if (user && user.permissions.length > 0) {
            return user.permissions;
          }
          return [];
        })
      );
  }

  getPagePermissions(moduleId: number): Observable<Permission[]> {
    const module = permissions.modules.find(m => m.moduleId === moduleId);
    if (!module || !module.pagePermissions) {
      return of([]);
    }

    return of(module.pagePermissions.filter(p => this.isAuthorized(p.id)))
      .pipe(
        delay(1)
      );
  }

  getRowPermissions(moduleId: number): Observable<Permission[]> {
    const module = permissions.modules.find(m => m.moduleId === moduleId);
    if (!module || !module.rowPermissions) {
      return of([]);
    }

    return of(module.rowPermissions.filter(p => this.isAuthorized(p.id)))
      .pipe(
        delay(1),
        // map(rp => {
        //   if (moduleId === 0) {
        //     throw new Error('An error occurred while processing your request');
        //   } else {
        //     return rp;
        //   }
        // })
      );
  }
  public get redirectUrl(): string {
    return MVLocalStorageService.getItem('__redirect__');
  }
  public set redirectUrl(value) {
    MVLocalStorageService.setItem('__redirect__', value);
  }

  clearData() {
    this.redirectUrl = null;
    MVLocalStorageService.removeSessionItem(SessionItems.thirdpartylist_filterForm);
    MVLocalStorageService.removeSessionItem(SessionItems.thirdPartylist_pageIndex);
    MVLocalStorageService.removeSessionItem(SessionItems.thirdPartylist_pageSize);
    MVLocalStorageService.removeSessionItem(SessionItems.thirdPartylist_sortOrder);
    MVLocalStorageService.removeSessionItem(SessionItems.thirdPartylist_sortBy);
  }

  userHasPermission(permission: PermissionType): boolean {
    const currentUserPermissions = this._currentUserSubject.value.permissions.map(p => p.id);
    return currentUserPermissions.includes(permission);
  }

  userHasAnyPermission(...permissionList: PermissionType[]): boolean {
    const currentUserPermissions = this._currentUserSubject.value.permissions.map(p => p.id);
    return permissionList.some(x => currentUserPermissions.includes(x));
  }

  userHasAllPermission(...permissionList: PermissionType[]): boolean {
    const currentUserPermissions = this._currentUserSubject.value.permissions.map(p => p.id);
    return permissionList.every(x => currentUserPermissions.includes(x));
  }

  public changeReceiveAlerts(receive: boolean): Observable<boolean> {
    return this._http.get(`${this.apiEndPoint}/Users/ChangeReciveAlerts?isAlertOn=${receive}`)
    // return of(null)
      .pipe(
        // delay(2000),
        map(() => {
          const user = {...this._currentUserSubject.value, emailAlerts: receive};
          MVLocalStorageService.setItem('current_user', JSON.stringify(user));
          this._currentUserSubject.next(user as any as UserModel);
          return receive;
        })
      )
  }

  public changeReceiveCDIWarnings(receive: boolean): Observable<boolean> {
    return this._http.get(`${this.apiEndPoint}/Users/ChangeReceiveCDIWarnings?isAlertOn=${receive}`)
    // return of(null)
      .pipe(
        // delay(2000),
        map(() => {
          const user = {...this._currentUserSubject.value, CDIWarnings: receive};
          MVLocalStorageService.setItem('current_user', JSON.stringify(user));
          this._currentUserSubject.next(user as any as UserModel);
          return receive;
        })
      )
  }

  public AddOrChangeCDIWarningsPeriod(periodicityId: PeriodTypes): Observable<void> {
    const dto: AddUserPeriodicityPreferenceDto = {
      periodicityId,
      moduleId: ModulesEnum.FEC_ConflictOfInterestsSurveyInbox
    }
    return this._http.post<void>(`${this.apiEndPoint}/Users/AddOrChangeCDIWarningsPeriod`, dto)
    // return of(null)
      .pipe(
        // delay(2000),
        map(() => {
          const user = {...this._currentUserSubject.value, CDIWarningsPeriod: periodicityId};
          MVLocalStorageService.setItem('current_user', JSON.stringify(user));
          this._currentUserSubject.next(user as any as UserModel);
        })
      )
  }
}

export function getClientSettings(): UserManagerSettings {
  environment.auth = {
    ...environment.auth,
    userStore: new WebStorageStateStore({ store: window.localStorage }) // Sobrescribir `userStore`
  };
  return environment.auth;
}


